import { IonButton, IonButtons, IonCol, IonGrid, IonIcon, IonRow } from '@ionic/react';
import { ApexOptions } from 'apexcharts';
import { closeCircleOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
// eslint-disable-next-line import/default
import Chart from 'react-apexcharts';

import styles from '@/pages/ReportV2/components/Modals/ModalContent.module.css';

import { Loading } from '@/components/Loading/Loading';

import { uuid } from '@/utils';

import { KinematicData, PelvisAngularExcursionGraphAnnotations } from '@/models/reportV2.model';

import { themeColors } from '@/theme/colors';

type PelvisAngularExcursionModalContentProps = {
  pelvisAngularExcursionGraphAnnotations: PelvisAngularExcursionGraphAnnotations;
  pelvisKinematicData: KinematicData;
  setShowZoomModal: any;
};

function createLegend(crpData: any): any[] {
  const crpLegend = crpData.map((entry: any) => {
    return {
      id: uuid(),
      name: entry.name,
      color: entry.color,
    };
  });

  const allStridesEntry = [
    {
      id: uuid(),
      name: 'All planes',
      color: '#4d4f5c',
    },
  ];

  return [...crpLegend, ...allStridesEntry];
}

export const PelvisAngularExcursionModalContent = ({
  pelvisAngularExcursionGraphAnnotations,
  pelvisKinematicData,
  setShowZoomModal,
}: PelvisAngularExcursionModalContentProps) => {
  const [loading, setLoading] = useState(true);
  const [legend, setLegend] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('All planes');
  const [dataLeft, setDataLeft] = useState<any[]>(pelvisKinematicData.allStrides.Left.series);
  const [dataRight, setDataRight] = useState<any[]>(pelvisKinematicData.allStrides.Right.series);

  // Tab change set the active tab and the data for the graphs.
  const tabChange = (name: string) => {
    localStorage.setItem('activeTab', name);
    setActiveTab(name);
    if (name === 'All planes') {
      setDataLeft(pelvisKinematicData.allStrides.Left.series);
      setDataRight(pelvisKinematicData.allStrides.Right.series);
    } else {
      setDataLeft([pelvisKinematicData.allStrides.Left.series.find((element: any) => element.name === name)]);
      setDataRight([pelvisKinematicData.allStrides.Right.series.find((element: any) => element.name === name)]);
    }
  };

  useEffect(() => {
    const activeTab = localStorage.getItem('activeTab');
    if (activeTab) {
      setActiveTab(activeTab);
    } else {
      localStorage.setItem('activeTab', 'All planes');
      setActiveTab('All planes');
    }
  });

  useEffect(() => {
    setLegend(createLegend(pelvisKinematicData.allStrides.Left.series));
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, [pelvisKinematicData]);

  //#region Graph options
  const options_Left: ApexOptions = {
    chart: {
      type: 'line',
      width: '100%',
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      animations: {
        enabled: false,
      },
      offsetX: -10,
    },
    xaxis: {
      type: 'numeric',
      labels: {
        style: {
          fontSize: '8px',
        },
      },
    },
    yaxis: {
      show: true,
      min: pelvisKinematicData.allStrides.Left.yAxis.yMin,
      max: pelvisKinematicData.allStrides.Left.yAxis.yMax,
      tickAmount: pelvisKinematicData.allStrides.Left.yAxis.tickAmount,
      labels: {
        formatter: function (value: number) {
          return value.toFixed(0);
        },
        style: {
          fontSize: '8px',
        },
      },
      axisTicks: {
        show: true,
      },
    },
    grid: {
      show: true,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    title: {
      text: 'Left',
      align: 'center',
      margin: 0,
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
        fontFamily: 'Montserrat',
        color: '#707070',
      },
    },
    subtitle: {
      text: 'Anterior tilt / Drop (-) | Posterior tilt / Hike (+)',
      align: 'center',
      margin: -10,
      floating: false,
      offsetY: 25,
      style: {
        fontSize: '10px',
        fontFamily: 'Montserrat',
      },
    },
    stroke: {
      width: 1,
      dashArray: [0, 5],
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '9px',
      offsetY: -10,
      markers: {
        width: 8,
        height: 8,
      },
      onItemClick: {
        toggleDataSeries: true,
      },
    },
    annotations: {
      xaxis: pelvisAngularExcursionGraphAnnotations.Left,
      yaxis: [
        {
          y: 0,
          borderColor: 'red',
        },
      ],
    },
    markers: {
      size: [0, 0, 2],
      strokeWidth: 0,
      showNullDataPoints: false,
    },
    tooltip: {
      enabled: false,
    },
  };

  const options_Right: ApexOptions = {
    chart: {
      type: 'line',
      width: '100%',
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      animations: {
        enabled: false,
      },
      offsetX: -10,
    },
    xaxis: {
      type: 'numeric',
      labels: {
        style: {
          fontSize: '8px',
        },
      },
    },
    yaxis: {
      show: true,
      min: pelvisKinematicData.allStrides.Left.yAxis.yMin,
      max: pelvisKinematicData.allStrides.Left.yAxis.yMax,
      tickAmount: pelvisKinematicData.allStrides.Left.yAxis.tickAmount,
      labels: {
        formatter: function (value: number) {
          return value.toFixed(0);
        },
        style: {
          fontSize: '8px',
        },
      },
      axisTicks: {
        show: true,
      },
    },
    grid: {
      show: true,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    title: {
      text: 'Right',
      align: 'center',
      margin: 0,
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
        fontFamily: 'Montserrat',
        color: '#707070',
      },
    },
    subtitle: {
      text: 'Anterior tilt / Drop (-) | Posterior tilt / Hike (+)',
      align: 'center',
      margin: -10,
      floating: false,
      offsetY: 25,
      style: {
        fontSize: '10px',
        fontFamily: 'Montserrat',
      },
    },
    stroke: {
      width: 1,
      dashArray: [0, 5],
    },
    fill: {
      colors: ['2066FA', '#829696'],
    },

    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '9px',
      markers: {
        width: 8,
        height: 8,
      },
      onItemClick: {
        toggleDataSeries: true,
      },
    },
    annotations: {
      xaxis: pelvisAngularExcursionGraphAnnotations.Right,
      yaxis: [
        {
          y: 0,
          borderColor: 'red',
        },
      ],
    },
    markers: {
      size: [0, 0, 2],
      strokeWidth: 0,
      showNullDataPoints: false,
    },
    tooltip: {
      enabled: false,
    },
  };

  //#endregion

  const cleanUp = () => {
    localStorage.setItem('activeTab', 'All planes');
    setShowZoomModal({ show: false, graph: '' });
  };

  return (
    <>
      <IonGrid className={styles.Modalcontent_container} fixed>
        <IonRow className={styles.variabilityLegend_Row}>
          <IonCol className='' size='11'>
            <div className={styles.variabilityLegend}>
              {legend.map((element, _) => {
                const color = element.color;
                const iconStyle = {
                  backgroundColor: color,
                };
                const iconStyleRight = {
                  backgroundColor: themeColors.colorRight,
                };

                if (element.name === 'Sagittal') {
                  return (
                    <div
                      className={
                        activeTab === element.name
                          ? styles.variabilityLegend_Entry_Selected
                          : styles.variabilityLegend_Entry
                      }
                      key={element.id}
                      onClick={() => {
                        tabChange(element.name);
                        console.log('element.name', element.name);
                      }}>
                      <div className={styles.legend_dot} style={iconStyle} />
                      <p className={styles.legend_name}>/</p>
                      <div className={styles.legend_dot} style={iconStyleRight} />
                      <p className={styles.legend_name}>{element.name}</p>
                    </div>
                  );
                }
                return (
                  <div
                    className={
                      activeTab === element.name
                        ? styles.variabilityLegend_Entry_Selected
                        : styles.variabilityLegend_Entry
                    }
                    key={element.id}
                    onClick={() => {
                      tabChange(element.name);
                    }}>
                    <div className={styles.legend_dot} style={iconStyle} />

                    <p className={styles.legend_name}>{element.name}</p>
                  </div>
                );
              })}
            </div>
          </IonCol>
          <IonCol className='ion-align-items-center' size='1'>
            <IonButtons className='ion-justify-content-center ion-align-self-center' style={{ height: '100%' }}>
              <IonButton className={styles.closeButton} fill='clear' onClick={() => cleanUp()} slot='icon-only'>
                <IonIcon icon={closeCircleOutline} />
              </IonButton>
            </IonButtons>
          </IonCol>
        </IonRow>
        <IonRow
          className='ion-align-items-center ion-align-items-stretch ion-padding-vertical ion-margin-vertical ion-justify-content-center'
          style={{ height: '75%' }}>
          {loading && <Loading />}
          {!loading && (
            <>
              <IonCol className='ion-no-padding' size='6'>
                <Chart height='90%' options={options_Left} series={dataLeft} type='line' width='100%' />
              </IonCol>
              <IonCol className='ion-no-padding' size='6'>
                <Chart height='90%' options={options_Right} series={dataRight} type='line' width='100%' />
              </IonCol>
            </>
          )}
        </IonRow>
      </IonGrid>
    </>
  );
};
